<template>
  <b-modal
    id="modal-automations"
    size="xl"
    header-class="align-items-center"
    @hidden="reset"
  >
    <template #modal-header="{ close }">
      <h5 class="mb-0">
        Automatizaciones
      </h5>
      <b-button
        variant="text"
        class="btn-close bg-transparent p-0 d-flex align-items-center justify-content-center"
        style="min-height: 24px"
        @click="close"
      >
        <i
          class="ri-close-line hp-text-color-dark-0 lh-1"
          style="font-size: 24px"
        />
      </b-button>
    </template>

    <div
      v-if="loading"
      class="text-center py-10"
    >
      <b-spinner
        small
        label="Cargando..."
      />
    </div>

    <b-tabs
      v-else
      pills
    >
      <b-tab
        title="Correos automáticos"
        active
      >
        <div class="d-flex justify-content-end mb-8">
          <b-button
            variant="primary"
            size="sm"
            @click="addEmailAction"
          >
            <i class="ri-add-line mr-4" />Agregar correo
          </b-button>
        </div>

        <div
          v-for="(action, index) in emailActions"
          :key="action.uid"
          class="border rounded hp-border-color-black-20 p-16 mb-16"
        >
          <div class="d-flex align-items-center justify-content-between mb-16">
            <h6 class="mb-0">
              Correo {{ index + 1 }}
            </h6>
            <b-button
              variant="danger"
              size="sm"
              @click="removeEmailAction(index)"
            >
              <i class="ri-delete-bin-line mr-4" />Eliminar
            </b-button>
          </div>

          <b-form-group label="Nombre de la acción">
            <b-form-input
              v-model="action.name"
              placeholder="Ej: Aviso a jefatura"
            />
          </b-form-group>

          <b-form-checkbox
            v-model="action.enabled"
            switch
            class="mb-16"
          >
            Habilitada
          </b-form-checkbox>

          <trigger-fields
            :trigger="action.trigger"
            :question-options="questionOptions"
          />

          <b-form-group label="¿De dónde se obtiene el correo de destino?">
            <b-form-radio-group
              v-model="action.email_mode"
              :options="emailModeOptions"
              name="email_mode"
              stacked
              @change="onEmailModeChange(index)"
            />
          </b-form-group>

          <b-form-group
            v-if="action.email_mode === 'fixed'"
            label="Correos de destino"
          >
            <b-form-input
              v-model="action.email_recipient"
              placeholder="Opcional - Ej: correo@dominio.cl (puedes separar varios con coma)"
            />
          </b-form-group>

          <b-form-group
            v-else
            label="Pregunta que contiene el correo de destino"
          >
            <v-select
              v-model="action.email_source_question"
              :options="emailSourceOptions"
              label="label"
              :reduce="(o) => o.name"
              placeholder="Seleccione una pregunta"
            />
          </b-form-group>

          <b-form-group label="Asunto del correo">
            <b-form-input
              v-model="action.subject"
              placeholder="Ej: Se detectó una condición insegura"
            />
          </b-form-group>

          <b-form-group label="Cuerpo del correo">
            <b-form-textarea
              v-model="action.body"
              rows="4"
            />
            <p class="text-muted mb-0 mt-8">
              Variables: <code>{project}</code>, <code>{code}</code>, <code>{place}</code>,
              <code>{fecha}</code>, <code>{checklist}</code>, <code>{business_unit}</code>,
              <code>{respuesta:NOMBRE PREGUNTA}</code>,
              <code>{question}</code> (pregunta del disparador),
              <code>{answer}</code> (respuesta del disparador),
              <code>{presupuesto}</code>, <code>{imagenes}</code>.
            </p>
          </b-form-group>

          <b-form-group label="Pregunta de la imagen a adjuntar (opcional)">
            <v-select
              v-model="action.images_question"
              :options="imageQuestionOptions"
              label="label"
              :reduce="(o) => o.name"
              placeholder="Seleccione una pregunta"
            />
          </b-form-group>
        </div>
      </b-tab>

      <b-tab title="Hojas de cálculo de Google">
        <div class="d-flex justify-content-end mb-8">
          <b-button
            variant="primary"
            size="sm"
            @click="addSheetAction"
          >
            <i class="ri-add-line mr-4" />Agregar fila de hoja
          </b-button>
        </div>

        <div
          v-for="(action, index) in sheetActions"
          :key="action.uid"
          class="border rounded hp-border-color-black-20 p-16 mb-16"
        >
          <div class="d-flex align-items-center justify-content-between mb-16">
            <h6 class="mb-0">
              Hoja {{ index + 1 }}
            </h6>
            <b-button
              variant="danger"
              size="sm"
              @click="removeSheetAction(index)"
            >
              <i class="ri-delete-bin-line mr-4" />Eliminar
            </b-button>
          </div>

          <b-form-group label="Nombre de la acción">
            <b-form-input
              v-model="action.name"
              placeholder="Ej: Guardar resultados"
            />
          </b-form-group>

          <b-form-checkbox
            v-model="action.enabled"
            switch
            class="mb-16"
          >
            Habilitada
          </b-form-checkbox>

          <b-form-group label="ID de la hoja de cálculo">
            <b-form-input
              v-model="action.spreadsheet_id"
              placeholder="ID de la hoja de cálculo de Google"
            />
          </b-form-group>

          <b-form-group label="Nombre de la pestaña">
            <b-form-input
              v-model="action.tab"
              placeholder="Ej: Resultados"
            />
          </b-form-group>

          <b-form-group label="Unidades de negocio (sufijos de la pestaña)">
            <v-select
              v-model="action.tab_units"
              multiple
              :options="businessUnitOptions"
              :reduce="(o) => o.code"
              label="label"
              placeholder="Seleccione una o más unidades de negocio"
            />
          </b-form-group>

          <trigger-fields
            :trigger="action.trigger"
            :question-options="questionOptions"
          />

          <h6 class="mt-24 mb-16">
            Columnas
          </h6>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="addColumn(action)"
          >
            <i class="ri-add-line mr-4" />Agregar columna
          </b-button>

          <div
            v-for="(column, colIndex) in action.columns"
            :key="`col-${colIndex}`"
            class="mt-16 p-16 rounded bg-black-0 hp-bg-color-dark-100"
          >
            <div class="d-flex align-items-center justify-content-between mb-8">
              <b-button
                variant="danger"
                size="sm"
                @click="removeColumn(action, colIndex)"
              >
                <i class="ri-delete-bin-line mr-4" />
              </b-button>
            </div>

            <b-form-group label="Encabezado de la columna">
              <b-form-input
                v-model="column.header"
                placeholder="Ej: CeCo del Proyecto"
              />
            </b-form-group>

            <b-form-group label="Fuente del dato">
              <v-select
                v-model="column.source"
                :options="columnSourceOptions"
                label="label"
                :reduce="(o) => o.value"
              />
            </b-form-group>

            <b-form-group
              v-if="column.source === 'question'"
              label="Pregunta"
            >
              <v-select
                v-model="column.question"
                :options="questionOptions"
                label="label"
                :reduce="(o) => o.name"
                placeholder="Seleccione una pregunta"
              />
            </b-form-group>

            <b-form-group
              v-else-if="column.source === 'average'"
              label="Preguntas a promediar"
            >
              <v-select
                v-model="column.questions"
                multiple
                :options="questionOptions"
                label="label"
                :reduce="(o) => o.name"
                placeholder="Seleccione una o más preguntas"
              />
            </b-form-group>

            <div v-else-if="column.source === 'weighted_average'">
              <div class="d-flex align-items-center justify-content-between mb-8">
                <span class="hp-p1-body">Grupos del promedio ponderado</span>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="addGroup(column)"
                >
                  <i class="ri-add-line mr-4" />Grupo
                </b-button>
              </div>
              <div
                v-for="(group, groupIndex) in column.groups"
                :key="`group-${groupIndex}`"
                class="p-12 rounded hp-border-color-black-20 border mb-8"
              >
                <div class="d-flex align-items-end">
                  <b-form-group
                    label="Peso (%)"
                    class="flex-shrink-0 mr-16 mb-0"
                  >
                    <b-form-input
                      v-model="group.weight"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </b-form-group>
                  <b-button
                    variant="danger"
                    size="sm"
                    class="mb-16"
                    @click="removeGroup(column, groupIndex)"
                  >
                    <i class="ri-delete-bin-line" />
                  </b-button>
                </div>
                <b-form-group label="Preguntas del grupo">
                  <v-select
                    v-model="group.questions"
                    multiple
                    :options="questionOptions"
                    label="label"
                    :reduce="(o) => o.name"
                    placeholder="Seleccione una o más preguntas"
                  />
                </b-form-group>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab title="Reglas de salto">
        <div class="d-flex justify-content-end mb-8">
          <b-button
            variant="primary"
            size="sm"
            @click="addSkipRule"
          >
            <i class="ri-add-line mr-4" />Agregar regla
          </b-button>
        </div>

        <div
          v-for="(rule, index) in skipRules"
          :key="`rule-${index}`"
          class="border rounded hp-border-color-black-20 p-16 mb-16"
        >
          <div class="d-flex align-items-center justify-content-between mb-16">
            <h6 class="mb-0">
              Regla {{ index + 1 }}
            </h6>
            <b-button
              variant="danger"
              size="sm"
              @click="removeSkipRule(index)"
            >
              <i class="ri-delete-bin-line mr-4" />Eliminar
            </b-button>
          </div>

          <b-form-group label="Pregunta de la condición">
            <v-select
              v-model="rule.condition_question"
              :options="questionOptions"
              label="label"
              :reduce="(o) => o.name"
              placeholder="Seleccione una pregunta"
            />
          </b-form-group>

          <b-form-group label="Valor de la condición">
            <b-form-input
              v-model="rule.condition_value"
              placeholder="Ej: no"
            />
          </b-form-group>

          <b-form-group label="Pregunta a saltar">
            <v-select
              v-model="rule.skip_question"
              :options="questionOptions"
              label="label"
              :reduce="(o) => o.name"
              placeholder="Seleccione una pregunta"
            />
          </b-form-group>
        </div>
      </b-tab>

      <b-tab title="Documentos">
        <b-form-checkbox
          v-model="documentFirmaEnabled"
          switch
          class="mb-16"
        >
          Habilitar firma en PDF
        </b-form-checkbox>

        <div :class="{ 'opacity-50 pointer-events-none': !documentFirmaEnabled }">
          <b-form-group label="Pregunta de firma">
            <v-select
              v-model="documentFirmaQuestion"
              :options="questionOptions"
              label="label"
              :reduce="(o) => o.name"
              placeholder="Seleccione una pregunta"
            />
          </b-form-group>
          <p class="text-muted">
            Si la pregunta tiene respuesta con texto, se usa como firma en el PDF. Si está vacía, no se incluye firma.
          </p>
        </div>
      </b-tab>

      <b-tab title="Presupuesto">
        <b-form-checkbox
          v-model="budgetAction.enabled"
          switch
          class="mb-16"
        >
          Habilitar cálculo de presupuesto
        </b-form-checkbox>

        <div :class="{ 'opacity-50 pointer-events-none': !budgetAction.enabled }">
          <h6 class="mt-24 mb-8">
            Tramos de Canalización
          </h6>
          <p class="text-muted mb-16">
            Mapea cada tramo a las preguntas del checklist que responden tipo de canalización, metros, tipo de conductor y metros.
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            class="mb-16"
            @click="addBudgetSegment"
          >
            <i class="ri-add-line mr-4" />Agregar tramo
          </b-button>

          <div
            v-for="(seg, segIndex) in budgetAction.segments"
            :key="`seg-${segIndex}`"
            class="border rounded hp-border-color-black-20 p-16 mb-16"
          >
            <div class="d-flex align-items-center justify-content-between mb-12">
              <h6 class="mb-0">
                {{ seg.name || 'Tramo ' + (segIndex + 1) }}
              </h6>
              <b-button
                variant="danger"
                size="sm"
                @click="removeBudgetSegment(segIndex)"
              >
                <i class="ri-delete-bin-line" />
              </b-button>
            </div>

            <b-form-group label="Nombre del tramo">
              <b-form-input
                v-model="seg.name"
                :placeholder="'Tramo ' + (segIndex + 1)"
              />
            </b-form-group>

            <b-row>
              <b-col md="6">
                <b-form-group label="Tipo de Canalización (select)">
                  <v-select
                    v-model="seg.canal_type"
                    :options="questionOptions"
                    label="label"
                    :reduce="(o) => o.name"
                    placeholder="Pregunta que responde el tipo de ducto"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Metros de Canalización (number)">
                  <v-select
                    v-model="seg.canal_meters"
                    :options="questionOptions"
                    label="label"
                    :reduce="(o) => o.name"
                    placeholder="Pregunta que responde los metros"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Tipo de Conductor (select)">
                  <v-select
                    v-model="seg.conductor_type"
                    :options="questionOptions"
                    label="label"
                    :reduce="(o) => o.name"
                    placeholder="Pregunta que responde el tipo de cable"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Metros de Conductor (number)">
                  <v-select
                    v-model="seg.conductor_meters"
                    :options="questionOptions"
                    label="label"
                    :reduce="(o) => o.name"
                    placeholder="Pregunta que responde los metros"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <h6 class="mt-24 mb-8">
            Adecuaciones
          </h6>
          <p class="text-muted mb-16">
            Elige una pregunta de selección múltiple de la plantilla. Cada opción marcada en terreno suma su adecuación al cálculo de presupuesto. Si la opción trae el código en el texto (ej: "Jornada (1-6-1)"), se usa automáticamente; también puedes escribirlo a mano.
          </p>

          <b-button
            variant="outline-primary"
            size="sm"
            class="mb-16"
            @click="addBudgetAdaptationGroup"
          >
            <i class="ri-add-line mr-4" />Agregar grupo de adecuaciones
          </b-button>

          <div
            v-for="(adapt, adaptIndex) in budgetAction.adaptations"
            :key="`adapt-${adaptIndex}`"
            class="border rounded hp-border-color-black-20 p-16 mb-16"
          >
            <div class="d-flex align-items-center justify-content-between mb-12">
              <h6 class="mb-0">
                Grupo de Adecuaciones {{ adaptIndex + 1 }}
              </h6>
              <b-button
                variant="danger"
                size="sm"
                @click="removeBudgetAdaptation(adaptIndex)"
              >
                <i class="ri-delete-bin-line" />
              </b-button>
            </div>

            <b-form-group label="Pregunta de selección múltiple (Listado Múltiple)">
              <v-select
                v-model="adapt.question"
                :options="budgetListOptions"
                label="label"
                :reduce="(o) => ({ id: o.id, name: o.name })"
                placeholder="Pregunta con selector múltiple de actividades"
                @input="onAdaptationQuestionChange(adapt)"
              />
            </b-form-group>

            <div
              v-if="adapt.loading"
              class="text-muted mb-8"
            >
              <b-spinner small class="mr-4" />Cargando opciones de la pregunta...
            </div>

            <div
              v-for="(item, itemIndex) in adapt.items"
              :key="`adapt-item-${itemIndex}`"
              class="d-flex align-items-center mb-8"
            >
              <span class="flex-grow-1 text-muted">
                {{ item.option }}
              </span>
              <b-form-input
                v-model="item.item_id"
                placeholder="ID adecuación (ej: 1-6-1)"
                size="sm"
                class="flex-shrink-0"
                style="width: 200px;"
              />
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <template #modal-footer>
      <b-button
        variant="light"
        @click="closeModal"
      >
        Cancelar
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving"
        @click="save"
      >
        <b-spinner
          v-if="saving"
          small
          class="mr-8"
        />
        Guardar
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { EMAIL_SOURCE_TYPES, QUESTION_TYPES, TYPE_GROUP } from '@/constants/question'
import TriggerFields from './TriggerFields.vue'

const IMAGE_SOURCE_TYPES = [
  QUESTION_TYPES.gallery_image,
  QUESTION_TYPES.image,
  QUESTION_TYPES.image_text,
  QUESTION_TYPES.images_text,
  QUESTION_TYPES.images_text2,
  QUESTION_TYPES.drawing_image,
]

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 6)
}

function extractItemCode(text) {
  const match = String(text || '').match(/\d+-\d+(?:-\d+)*/)
  return match ? match[0] : ''
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

const newTrigger = () => ({
  type: 'answer',
  question: '',
  value: '',
  condition: { question: '', value: '' },
})

const newEmailAction = () => ({
  uid: uid(),
  name: '',
  enabled: true,
  trigger: newTrigger(),
  email_mode: 'question',
  email_source_question: '',
  email_recipient: '',
  subject: '',
  body: '',
  images_question: '',
})

const newSheetAction = () => ({
  uid: uid(),
  name: '',
  enabled: true,
  spreadsheet_id: '',
  tab: '',
  tab_units: [],
  trigger: newTrigger(),
  columns: [],
})

const newBudgetSegment = () => ({
  name: '',
  canal_type: '',
  canal_meters: '',
  conductor_type: '',
  conductor_meters: '',
})

const newSkipRule = () => ({
  condition_question: '',
  condition_value: '',
  skip_question: '',
})

export default {
  name: 'UpdateOrCreateAutomations',
  components: { TriggerFields },
  data() {
    return {
      loading: false,
      saving: false,
      emailActions: [],
      sheetActions: [],
      skipRules: [],
      documentFirmaEnabled: false,
      documentFirmaQuestion: '',
      budgetAction: { enabled: false, segments: [], adaptations: [] },
      download: { enabled: false, percent: 100 },
      columnSourceOptions: [
        { value: 'question', label: 'Pregunta' },
        { value: 'average', label: 'Promedio de preguntas' },
        { value: 'weighted_average', label: 'Promedio ponderado' },
        { value: 'business_unit', label: 'Unidad de negocio' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      checklist: 'checklist/checklist',
      questions: 'question/questions',
      businessUnits: 'project/businessUnits',
    }),
    questionOptions() {
      return this.flattenedQuestions
        .filter(q => !q.question_type || q.question_type !== TYPE_GROUP)
        .map(q => ({ name: q.name, label: q.label }))
    },
    emailSourceOptions() {
      return this.flattenedQuestions
        .filter(q => EMAIL_SOURCE_TYPES.includes(q.question_type))
        .map(q => ({ name: q.name, label: q.label }))
    },
    budgetListOptions() {
      return this.flattenedQuestions
        .filter(q => q.question_type === QUESTION_TYPES.multiple_select)
        .map(q => ({ id: q.id, name: q.name, label: q.label }))
    },
    imageQuestionOptions() {
      return this.flattenedQuestions
        .filter(q => IMAGE_SOURCE_TYPES.includes(q.question_type))
        .map(q => ({ name: q.name, label: q.label }))
    },
    emailModeOptions() {
      return [
        { value: 'question', text: 'Extraer de una pregunta' },
        { value: 'fixed', text: 'Escribir correo específico' },
      ]
    },
    businessUnitOptions() {
      return (this.businessUnits || []).map(unit => {
        const code = unit.sap_code || unit.name
        return {
          code,
          label: code,
        }
      })
    },
    flattenedQuestions() {
      return this.flattenQuestions(this.questions)
    },
  },
  methods: {
    ...mapActions({
      getQuestions: 'question/getQuestions',
      getQuestionOptionsFromQuestion: 'question/getQuestionOptionsFromQuestion',
      getBusinessUnits: 'project/getBusinessUnits',
      updateOrCreateChecklist: 'checklist/updateOrCreateChecklist',
    }),
    flattenQuestions(list, groupName = null) {
      let result = []
      list.forEach(question => {
        const parentName = question.parent
          ? question.parent.name || groupName
          : groupName
        result.push({
          id: question.id,
          name: question.name,
          label: parentName
            ? `${question.name} — (${parentName})`
            : question.name,
          question_type: question.question_type ? question.question_type.id : null,
        })
        if (question.child && question.child.length) {
          result = result.concat(this.flattenQuestions(question.child, parentName))
        }
      })
      return result
    },
    async openModal() {
      this.loading = true
      await this.getQuestions({
        checklist_id: this.checklist.id,
        not_render_answers: true,
      })
      await this.getBusinessUnits()
      const automations = this.checklist.automations || {}
      this.download = clone(
        automations.download || { enabled: false, percent: 100 },
      )
      this.emailActions = clone(automations.email_actions || [])
      this.sheetActions = clone(automations.sheet_actions || [])
      this.skipRules = clone(automations.skip_rules || [])
      const docActions = automations.document_actions || []
      const firstDoc = docActions[0] || {}
      this.documentFirmaEnabled = (firstDoc.firma && firstDoc.firma.enabled) || false
      this.documentFirmaQuestion = (firstDoc.firma && firstDoc.firma.question) || ''
      this.budgetAction = clone(automations.budget_action || { enabled: false, segments: [], adaptations: [] })
      this.normalize()
      this.loading = false
      this.$bvModal.show('modal-automations')
    },
    normalize() {
      const ensureTrigger = trigger => {
        if (!trigger) return newTrigger()
        if (!trigger.condition) {
          return { ...trigger, condition: { question: '', value: '' } }
        }
        return trigger
      }
      this.emailActions = this.emailActions.map(action => ({
        ...action,
        email_mode: action.email_mode || (action.email_recipient ? 'fixed' : 'question'),
        email_recipient:
          action.email_mode === 'fixed' || action.email_recipient
            ? action.email_recipient || ''
            : '',
        email_source_question:
          action.email_mode === 'question' || action.email_source_question
            ? action.email_source_question || ''
            : '',
        trigger: ensureTrigger(action.trigger),
      }))
      this.sheetActions = this.sheetActions.map(action => ({
        ...action,
        trigger: ensureTrigger(action.trigger),
        columns: (action.columns || []).map(column => ({
          ...column,
          questions: column.questions || [],
          groups: column.groups || [],
        })),
      }))
      this.budgetAction.adaptations = (this.budgetAction.adaptations || []).map(
        adapt => ({
          loading: false,
          question: this.resolveQuestionRef(adapt.question),
          items: (adapt.items || [])
            .filter(item => item.option)
            .map(item => ({
              option: item.option,
              item_id: item.item_id || extractItemCode(item.option),
            })),
        })
      )
    },
    resolveQuestionRef(ref) {
      if (!ref) return null
      if (typeof ref !== 'object') {
        const match = this.flattenedQuestions.find(q => q.name === ref)
        return match ? { id: match.id, name: match.name } : { name: ref }
      }
      if (ref.id) return { id: ref.id, name: ref.name }
      const match = this.flattenedQuestions.find(q => q.name === ref.name)
      return match ? { id: match.id, name: match.name } : { name: ref.name }
    },
    reset() {
      this.emailActions = []
      this.sheetActions = []
      this.skipRules = []
      this.documentFirmaEnabled = false
      this.documentFirmaQuestion = ''
      this.budgetAction = { enabled: false, segments: [], adaptations: [] }
      this.download = { enabled: false, percent: 100 }
    },
    closeModal() {
      this.$bvModal.hide('modal-automations')
    },
    addEmailAction() {
      this.emailActions.push(newEmailAction())
    },
    removeEmailAction(index) {
      this.emailActions.splice(index, 1)
    },
    onEmailModeChange(index) {
      const action = this.emailActions[index]
      if (!action) return
      if (action.email_mode === 'question') {
        action.email_recipient = ''
      } else {
        action.email_source_question = ''
      }
    },
    addSheetAction() {
      this.sheetActions.push(newSheetAction())
    },
    removeSheetAction(index) {
      this.sheetActions.splice(index, 1)
    },
    addColumn(action) {
      action.columns.push({
        header: '',
        source: 'question',
        question: '',
        questions: [],
        groups: [],
      })
    },
    removeColumn(action, index) {
      action.columns.splice(index, 1)
    },
    addGroup(column) {
      column.groups.push({ questions: [], weight: 0 })
    },
    removeGroup(column, index) {
      column.groups.splice(index, 1)
    },
    addSkipRule() {
      this.skipRules.push(newSkipRule())
    },
    removeSkipRule(index) {
      this.skipRules.splice(index, 1)
    },
    addBudgetSegment() {
      this.budgetAction.segments.push(newBudgetSegment())
    },
    removeBudgetSegment(index) {
      this.budgetAction.segments.splice(index, 1)
    },
    addBudgetAdaptationGroup() {
      this.budgetAction.adaptations.push({
        question: null,
        loading: false,
        items: [],
      })
    },
    removeBudgetAdaptation(index) {
      this.budgetAction.adaptations.splice(index, 1)
    },
    addManualAdaptation(adapt) {
      adapt.items.push({ option: '', item_id: '' })
    },
    async onAdaptationQuestionChange(adapt) {
      if (!adapt || !adapt.question || !adapt.question.id) return
      adapt.loading = true
      try {
        const options = await this.getQuestionOptionsFromQuestion(
          adapt.question.id,
        )
        const existing = {}
        const manual = []
        adapt.items.forEach(item => {
          if (item.option) {
            existing[item.option] = item.item_id
          } else {
            manual.push(item)
          }
        })
        const items = (options || []).map(option => ({
          option: option.name,
          item_id: existing[option.name] || extractItemCode(option.name) || '',
        }))
        adapt.items = [...manual, ...items]
      } catch (error) {
        adapt.items = adapt.items.filter(item => !item.option)
      } finally {
        adapt.loading = false
      }
    },
    cleanColumn(column) {
      const base = { header: column.header, source: column.source }
      if (column.source === 'question') {
        base.question = column.question
      } else if (column.source === 'average') {
        base.questions = (column.questions || []).filter(Boolean)
      } else if (column.source === 'weighted_average') {
        base.groups = (column.groups || []).map(group => ({
          questions: (group.questions || []).filter(Boolean),
          weight: Number(group.weight) || 0,
        }))
      }
      return base
    },
    cleanTrigger(trigger) {
      const clean = {
        type: trigger.type,
        question: trigger.question,
        value: trigger.value,
      }
      if (trigger.condition && trigger.condition.question) {
        clean.condition = {
          question: trigger.condition.question,
          value: trigger.condition.value,
        }
      }
      return clean
    },
    async save() {
      this.saving = true
      const automations = {
        download: this.download,
        email_actions: this.emailActions.map(action => ({
          uid: action.uid,
          name: action.name,
          enabled: action.enabled,
          trigger: this.cleanTrigger(action.trigger),
          email_mode: action.email_mode,
          email_source_question:
            action.email_mode === 'question' ? action.email_source_question : '',
          email_recipient:
            action.email_mode === 'fixed' ? action.email_recipient : '',
          subject: action.subject,
          body: action.body,
          images_question: action.images_question,
        })),
        sheet_actions: this.sheetActions.map(action => ({
          uid: action.uid,
          name: action.name,
          enabled: action.enabled,
          spreadsheet_id: action.spreadsheet_id,
          tab: action.tab,
          tab_units: action.tab_units,
          trigger: this.cleanTrigger(action.trigger),
          columns: action.columns.map(column => this.cleanColumn(column)),
        })),
        skip_rules: this.skipRules.map(rule => ({
          condition_question: rule.condition_question,
          condition_value: rule.condition_value,
          skip_question: rule.skip_question,
        })),
        document_actions: this.documentFirmaQuestion ? [{
          uid: 'document_firma',
          name: 'Firma PDF',
          enabled: this.documentFirmaEnabled,
          firma: {
            enabled: this.documentFirmaEnabled,
            question: this.documentFirmaQuestion,
          },
        }] : [],
        budget_action: {
          enabled: this.budgetAction.enabled,
          segments: this.budgetAction.segments.map(seg => ({
            name: seg.name,
            canal_type: seg.canal_type,
            canal_meters: seg.canal_meters,
            conductor_type: seg.conductor_type,
            conductor_meters: seg.conductor_meters,
          })),
          adaptations: this.budgetAction.adaptations
            .filter(a => a.question && a.question.name)
            .map(a => ({
              question: a.question.name,
              items: (a.items || [])
                .filter(item => item.item_id)
                .map(item => ({
                  option: item.option || '',
                  item_id: item.item_id,
                })),
            })),
        },
      }
      const response = await this.updateOrCreateChecklist({
        id: this.checklist.id,
        automations,
      })
      this.saving = false
      if (response.status === 200) {
        this.$bvToast.toast('Automatizaciones actualizadas correctamente', {
          title: 'Excelente',
          variant: 'primary',
          solid: false,
        })
        this.closeModal()
        this.$emit('success')
      } else {
        this.$bvToast.toast('No se pudieron guardar las automatizaciones', {
          title: 'Error',
          variant: 'danger',
          solid: false,
        })
      }
    },
  },
}
</script>
